import useApiRequest from "@/lib/hooks/use-request";
import {
  InvitationListResponseDto,
  UserListPaginatedResponseDtoWithInvitationCount,
  UserListResponseDto,
} from "@/lib/interfaces/users";
import { Paginated, Pagination } from "@/lib/interfaces/utils";
import DataFetchParent from "@/ui-components/ui/data-fetch";
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import InvitationsDataTable from "./data-tables/invitations-data-table";
import UsersDataTable from "./data-tables/user-data-table";

const UsersFetchParent = ({
  showInvitations,
  setInvitationCount,
  searchQuery,
  setReload,
  reload,
}: {
  showInvitations: boolean;
  setInvitationCount: Dispatch<SetStateAction<number>>;
  searchQuery: string;
  setReload: Dispatch<SetStateAction<boolean>>;
  reload: boolean;
}) => {
  return (
    <>
      {showInvitations ? (
        <InvitationsFetcher searchQuery={searchQuery} setReload={setReload} reload={reload} />
      ) : (
        <UsersFetcher
          searchQuery={searchQuery}
          setInvitationCount={setInvitationCount}
          setReload={setReload}
          reload={reload}
        />
      )}
    </>
  );
};

const UsersFetcher = ({
  searchQuery,
  setInvitationCount,
  setReload,
  reload,
}: {
  searchQuery: string;
  setInvitationCount: Dispatch<SetStateAction<number>>;
  setReload: Dispatch<SetStateAction<boolean>>;
  reload: boolean;
}) => {
  const [items, setItems] = useState<UserListResponseDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const { apiRequest } = useApiRequest();
  const [pagination, setPagination] = useState<Pagination>({
    pageIndex: searchQuery.split("page=")[1] ? parseInt(searchQuery.split("page=")[1].split("&")[0]) - 1 : 0,
    pageSize: 3,
  });

  const [pageCount, setPageCount] = useState<number>(0);

  const getItemsInit = useCallback(async () => {
    setInitLoading(true);
    const response = await apiRequest<UserListPaginatedResponseDtoWithInvitationCount>("settings/users/initial", "GET");
    setInitLoading(false);
    if (response.data) {
      setItems(response.data.users.docs);
      setInvitationCount(response.data.invitationCount);
      setPageCount(response.data.users.pageCount);
    }
  }, [apiRequest, setInvitationCount]);

  const getItemsPaginated = useCallback(async () => {
    setIsLoading(true);
    let url = `settings/users?limit=${pagination.pageSize}&page=${pagination.pageIndex + 1}`;
    if (searchQuery) {
      url = `settings/users/search?limit=${pagination.pageSize}&page=${pagination.pageIndex + 1}&search=${searchQuery}`;
    }
    const response = await apiRequest<Paginated<UserListResponseDto>>(url, "GET");
    setIsLoading(false);
    if (response.data) {
      setItems(response.data.docs);
      setPageCount(response.data.pageCount);
    }
  }, [apiRequest, pagination.pageIndex, pagination.pageSize, searchQuery]);

  useEffect(() => {
    getItemsInit();
  }, [getItemsInit]);

  useEffect(() => {
    if (!initLoading) {
      getItemsPaginated();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, getItemsPaginated, searchQuery, reload]);

  return (
    <DataFetchParent
      data={items}
      isLoading={isLoading || initLoading}
      renderElement={(response: UserListResponseDto[]) => (
        <div className="w-full p-4">
          <UsersDataTable
            data={response}
            isLoading={isLoading || initLoading}
            pageCount={pageCount}
            pagination={pagination}
            setPagination={setPagination}
            setReload={setReload}
          />
        </div>
      )}
    />
  );
};

const InvitationsFetcher = ({
  searchQuery,
  setReload,
  reload,
}: {
  searchQuery: string;
  setReload: Dispatch<SetStateAction<boolean>>;
  reload: boolean;
}) => {
  const [items, setItems] = useState<InvitationListResponseDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { apiRequest } = useApiRequest();
  const [pagination, setPagination] = useState<Pagination>({
    pageIndex: searchQuery.split("page=")[1] ? parseInt(searchQuery.split("page=")[1].split("&")[0]) - 1 : 0,
    pageSize: 3,
  });

  const [pageCount, setPageCount] = useState<number>(0);

  const getItems = useCallback(async () => {
    setIsLoading(true);
    let url = `settings/users/invitations?limit=${pagination.pageSize}&page=${pagination.pageIndex + 1}`;
    if (searchQuery) {
      url += `&search=${searchQuery}`;
    }
    const response = await apiRequest<Paginated<InvitationListResponseDto>>(url, "GET");
    setIsLoading(false);
    if (response.data) {
      setItems(response.data.docs);
      setPageCount(response.data.pageCount);
    }
  }, [apiRequest, pagination.pageIndex, pagination.pageSize, searchQuery]);

  useEffect(() => {
    getItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, searchQuery, reload]);

  return (
    <DataFetchParent
      data={items}
      isLoading={isLoading}
      renderElement={(response: InvitationListResponseDto[]) => (
        <div className="w-full p-4">
          <InvitationsDataTable
            data={response}
            isLoading={isLoading}
            pageCount={pageCount}
            pagination={pagination}
            setPagination={setPagination}
            setReload={setReload}
          />
        </div>
      )}
    />
  );
};

export default UsersFetchParent;
